import React from 'react';
import { useSelector } from 'react-redux';
import classNames from 'classnames';
import { useGetContactMe, useMarkContactMeAsRead } from '@games/api';
import { UserInfo } from '@games/types';
import { selectAuthUser } from '@games/ui';
import { FormattedMessage } from 'react-intl';
import intlMessages from 'intl/messages';

const isBarry = (me: UserInfo | null) => me && me.username === 'barry';

const ContactsPage: React.FC = () => {
  const me = useSelector(selectAuthUser);
  const [{ loading, data: messages }, getContactMe] = useGetContactMe();
  const [{ data: markData }, markContactMeAsRead] = useMarkContactMeAsRead();
  const [awaitRefresh, setAwaitRefresh] = React.useState(false);
  const sortedMessages = React.useMemo(() => {
    if (!messages) {
      return [];
    }
    return [...messages].sort((a, b) => {
      if (a.is_read && !b.is_read) {
        return 1;
      }
      if (!a.is_read && b.is_read) {
        return -1;
      }
      return b.inserted.localeCompare(a.inserted);
    });
  }, [messages]);

  React.useEffect(() => {
    if (!isBarry(me)) {
      return;
    }
    getContactMe();
  }, [me, getContactMe]);

  const markAsRead = React.useCallback(
    (id: string) => {
      markContactMeAsRead({ data: { id, is_read: true } });
      setAwaitRefresh(true);
    },
    [markContactMeAsRead],
  );
  React.useEffect(() => {
    if (awaitRefresh && markData) {
      getContactMe();
      setAwaitRefresh(false);
    }
  }, [awaitRefresh, markData, getContactMe]);

  if (!isBarry(me)) {
    return null;
  }

  if (loading) {
    return (
      <div>
        <FormattedMessage {...intlMessages.loading} />
      </div>
    );
  }

  return (
    <div className='container mx-auto'>
      <table
        width='100%'
        className={classNames(
          '[&_th]:p-3 [&_td]:p-3 border-gray-300',
          '[&>tbody>tr:nth-child(odd)]:bg-gray-200',
          'dark:[&>tbody>tr:nth-child(odd)]:bg-gray-700',
        )}
      >
        <thead>
          <tr className='font-bold border-b-2 [&_th]:text-left'>
            <th></th>
            <th>
              <FormattedMessage {...intlMessages.name} />
            </th>
            <th>
              <FormattedMessage {...intlMessages.email} />
            </th>
            <th>
              <FormattedMessage {...intlMessages.message} />
            </th>
            <th>
              <FormattedMessage {...intlMessages.created} />
            </th>
          </tr>
        </thead>
        <tbody>
          {sortedMessages.map((message) => (
            <tr key={message.id}>
              <td>
                {!message.is_read && (
                  <button
                    onClick={() => {
                      markAsRead(message.id);
                    }}
                    className='btn btn-primary btn-sm'
                  >
                    <FormattedMessage {...intlMessages.markAsRead} />
                  </button>
                )}
              </td>
              <td>{message.name}</td>
              <td>{message.email}</td>
              <td>{message.message}</td>
              <td>{message.inserted}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default ContactsPage;
